/**
 * OTA Live Update Service
 * 
 * Capgo capacitor-updater를 사용한 OTA 번들 업데이트 관리.
 * Firestore에서 직접 설정/번들 정보를 읽고, Capgo로 다운로드/적용.
 * 오프라인 시 즉시 스킵, 불안정 네트워크 시 3초 타임아웃.
 * 
 * Firestore 구조 (프로젝트별 독립):
 *   ota/config                  — 전역 설정 (minNativeVersion, storeUrls 등)
 *   ota/config/releases/{ver}   — 번들별 메타데이터
 * 
 * 버전 형식: package.json의 version을 직접 사용
 *   3자리 = 네이티브 스토어 버전 (예: 1.3.0)
 *   5자리 = OTA 번들 버전 (예: 1.3.0.2.1 = 네이티브 1.3.0, 기능 2, 버그수정 1)
 *   nativeVersion은 앞 3자리에서 자동 추출
 * 
 * 업데이트 흐름:
 *   1. Firestore → 네이티브 앱 버전 체크 (강제/권고)
 *   2. Firestore → 호환되는 최신 번들 조회
 *   3. silent: 다운로드 → 다음 앱 재시작 시 자동 적용
 *      flexible: 다운로드 팝업 → 사용자 확인 후 적용
 *      forced: 다운로드 → 즉시 적용
 *   4. 앱 시작 시 notifyAppReady() 호출 → 번들 확정 (미호출 시 자동 롤백)
 */

import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { CapacitorUpdater, BundleInfo } from '@capgo/capacitor-updater';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { Fire } from '../FireClient';

// ── 인터페이스 ──────────────────────────────────────

/** 네이티브 앱 업데이트 체크 결과 */
export type NativeUpdateStatus = 
  | { type: 'up-to-date' }
  | { type: 'recommended'; storeUrl: string }
  | { type: 'forced'; storeUrl: string };

/** OTA 번들 업데이트 체크 결과 */
export type BundleUpdateStatus =
  | { type: 'up-to-date' }
  | { type: 'available'; bundle: BundleMetadata };

interface BundleMetadata {
  fullVersion: string;
  url: string;
  checksum: string;
  mode: 'silent' | 'flexible' | 'forced';
}

// ── 유틸리티 ──────────────────────────────────────

function compareVersions(a: string, b: string): number {
  // "1.4.0.4.1-rc.2" 같은 rc 프리릴리즈 접미사 지원:
  //   dev OTA 는 rc 태그마다 버전이 …-rc.N 으로 올라가 매 라운드 전파됨.
  //   정식(접미사 없음)은 어떤 rc.N 보다 높다 (semver: 1.4.0.4.1 > 1.4.0.4.1-rc.9).
  //   prod 는 접미사 없는 버전만 쓰므로 rc 값이 항상 MAX_SAFE_INTEGER → 기존 동작 그대로.
  const parse = (v: string) => {
    const [base, rc] = v.split('-rc.');
    return {
      parts: base.split('.').map(Number),
      rc: rc !== undefined ? (parseInt(rc, 10) || 0) : Number.MAX_SAFE_INTEGER,
    };
  };
  const pa = parse(a), pb = parse(b);
  const len = Math.max(pa.parts.length, pb.parts.length);
  for (let i = 0; i < len; i++) {
    const na = pa.parts[i] || 0;
    const nb = pb.parts[i] || 0;
    if (na !== nb) return na - nb;
  }
  return pa.rc - pb.rc;
}

// ── 업데이트 전용 화면 (DOM 기반, 의존성 없음) ──────────────────

const otaLang = (() => {
  const l = ((navigator.languages && navigator.languages[0]) || localStorage.getItem('app-lang') || navigator.language || '').slice(0, 2);
  return l === 'ko' ? 'ko' : l === 'ja' ? 'ja' : 'en';
})();

const otaTexts = {
  ko: { checking: '업데이트 확인 중...', downloading: '업데이트 다운로드 중...', applying: '업데이트 적용 중...', wait: '잠시만 기다려 주세요', soon: '곧 완료됩니다' },
  ja: { checking: 'アップデートを確認中...', downloading: 'アップデートをダウンロード中...', applying: 'アップデートを適用中...', wait: 'しばらくお待ちください', soon: 'まもなく完了します' },
  en: { checking: 'Checking for updates...', downloading: 'Downloading update...', applying: 'Applying update...', wait: 'Please wait', soon: 'Almost done' },
} as const;

const t = otaTexts[otaLang];

function showUpdateScreen() {
  if (document.getElementById('ota-update-screen')) return;

  const el = document.createElement('div');
  el.id = 'ota-update-screen';
  el.innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:24px;">
      <img src="assets/img/logo.png" alt="" style="width:140px;height:auto;" />
      <div style="text-align:center;">
        <p id="ota-status" style="font-size:15px;font-weight:600;color:rgb(var(--text-title, 51 51 51));margin:0 0 8px;">${t.downloading}</p>
        <p id="ota-sub" style="font-size:12px;color:rgb(var(--text-body, 153 153 153));margin:0;opacity:0.6;">${t.wait}</p>
      </div>
      <div style="width:200px;height:4px;background:rgb(var(--muted, 238 238 238));border-radius:2px;overflow:hidden;">
        <div id="ota-bar" style="width:0%;height:100%;background:linear-gradient(90deg,#667eea,#764ba2);border-radius:2px;transition:width 0.3s;"></div>
      </div>
      <p id="ota-percent" style="font-size:12px;color:rgb(var(--text-body, 187 187 187));margin:0;opacity:0.5;">0%</p>
    </div>
  `;
  Object.assign(el.style, {
    position: 'fixed', inset: '0', zIndex: '100001',
    background: 'rgb(var(--background, 255 255 255))',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  });
  document.body.appendChild(el);
}

function updateProgress(percent: number) {
  const bar = document.getElementById('ota-bar');
  const pct = document.getElementById('ota-percent');
  if (bar) bar.style.width = `${percent}%`;
  if (pct) pct.textContent = `${percent}%`;
}

function updateStatus(message: string, sub?: string) {
  const status = document.getElementById('ota-status');
  const subEl = document.getElementById('ota-sub');
  if (status) status.textContent = message;
  if (subEl && sub) subEl.textContent = sub;
}

function hideUpdateScreen() {
  const el = document.getElementById('ota-update-screen');
  if (el) el.remove();
}

// ── 메인 서비스 ──────────────────────────────────────

class OtaUpdateService {
  private initialized = false;
  private nativeVersion: string = '0.0.0';
  private platform: 'ios' | 'android' | 'web' = 'web';
  private currentBundleVersion?: string;

  // 세션당 1회 실행 가드
  private updateFlowRan = false;

  // OTA 흐름 완료 게이트 — 앱 데이터 로딩은 이 Promise가 resolve된 후에만 시작
  private _readyResolver!: () => void;
  readonly ready: Promise<void> = new Promise(r => { this._readyResolver = r; });

  // 캐시된 체크 결과
  private _nativeStatus: NativeUpdateStatus = { type: 'up-to-date' };
  private _bundleStatus: BundleUpdateStatus = { type: 'up-to-date' };

  /** 서비스 초기화 — 앱 시작 시 1회 호출 */
  async init(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      // console.log('[OTA] Web platform — OTA skipped');
      this._readyResolver();
      return;
    }

    console.log('[OTA] init() started');

    // 안전 타임아웃: init()이 어디서든 멈추면 10초 후 강제 진행
    const safetyTimeout = setTimeout(() => {
      console.warn('[OTA] init() safety timeout (10s) — forcing ready');
      this.removeSplash();
      this._readyResolver();
    }, 10000);

    try {
      const t0 = performance.now();
      this.platform = Capacitor.getPlatform() as 'ios' | 'android';

      console.log('[OTA] Calling App.getInfo()...');
      const appInfo = await CapApp.getInfo();
      const t1 = performance.now();
      console.log(`[OTA] App.getInfo() done in ${(t1-t0).toFixed(0)}ms — version: ${appInfo.version}`);

      this.nativeVersion = appInfo.version;
      this.currentBundleVersion = localStorage.getItem('ota-bundle-version') || undefined;

      // 네이티브 버전이 바뀌었으면 캐시된 OTA 번들 초기화
      const lastNativeVersion = localStorage.getItem('ota-native-version');
      if (lastNativeVersion && lastNativeVersion !== this.nativeVersion) {
        console.log(`[OTA] Native version changed (${lastNativeVersion} → ${this.nativeVersion}) — resetting to built-in bundle`);
        localStorage.removeItem('ota-bundle-version');
        localStorage.setItem('ota-native-version', this.nativeVersion);
        this.currentBundleVersion = undefined;
        // reset()은 WebView를 리로드할 수 있으므로 맨 마지막에 호출
        clearTimeout(safetyTimeout);
        await CapacitorUpdater.reset();
        return; // reset() 이후 WebView 리로드 → init()이 다시 호출됨
      }
      localStorage.setItem('ota-native-version', this.nativeVersion);

      // ✅ 현재 번들이 정상 작동함을 Capgo에 알림
      console.log('[OTA] Calling notifyAppReady()...');
      try {
        await Promise.race([
          CapacitorUpdater.notifyAppReady(),
          new Promise(r => setTimeout(r, 3000)),
        ]);
      } catch (e) {
        console.warn('[OTA] notifyAppReady failed (non-fatal):', e);
      }
      const t2 = performance.now();

      this.initialized = true;
      clearTimeout(safetyTimeout);
      console.log(`[OTA] Initialized — platform: ${this.platform}, version: ${this.nativeVersion}, bundle: ${this.currentBundleVersion || 'built-in'} | getInfo: ${(t1-t0).toFixed(0)}ms, total: ${(t2-t0).toFixed(0)}ms`);
    } catch (error) {
      console.error('[OTA] Init failed:', error);
      clearTimeout(safetyTimeout);
      this.removeSplash();
      this._readyResolver();
    }
  }

  /**
   * Firestore에서 OTA 업데이트를 체크.
   * 
   * 최적화: releases 쿼리를 먼저 실행 (~200ms).
   * release에 minNativeVersion이 명시된 경우에만 configDoc을 추가 읽기.
   */
  async checkForUpdates(): Promise<void> {
    if (!this.initialized) return;

    // 오프라인이면 체크 자체를 건너뜀
    try {
      const { Network } = await import('@capacitor/network');
      const status = await Network.getStatus();
      if (!status.connected) {
        console.log('[OTA] Offline — skipping update check');
        return;
      }
    } catch { /* Network 플러그인 없으면 계속 진행 */ }

    try {
      const t0 = performance.now();

      // ── 1. OTA 번들 체크 (빠름: ~200ms, 오프라인 시 3초 타임아웃) ──
      const releasesRef = collection(Fire.store, 'ota', 'config', 'releases');
      const bundleQuery = query(
        releasesRef,
        where('nativeVersion', '==', this.nativeVersion),
        where('active', '==', true),
      );

      const bundleSnap = await Promise.race([
        getDocs(bundleQuery),
        new Promise<null>(r => setTimeout(() => r(null), 3000)),
      ]);

      if (!bundleSnap) {
        console.log('[OTA] Check timed out (offline?) — skipping');
        return;
      }
      const t1 = performance.now();

      let needsNativeCheck = false;

      if (!bundleSnap.empty) {
        // fullVersion 기준 내림차순 정렬
        const sorted = bundleSnap.docs
          .map(d => d.data())
          .sort((a, b) => compareVersions(b.fullVersion || '0', a.fullVersion || '0'));
        const bundle = sorted[0];
        const latestVersion = bundle.fullVersion;

        // release에 checkNativeUpdate 플래그가 있으면 configDoc 읽기
        if (bundle.checkNativeUpdate) {
          needsNativeCheck = true;
        }

        if (!this.currentBundleVersion || compareVersions(latestVersion, this.currentBundleVersion) > 0) {
          this._bundleStatus = {
            type: 'available',
            bundle: {
              fullVersion: latestVersion,
              url: bundle.url,
              checksum: bundle.checksum,
              mode: bundle.mode || 'silent',
            },
          };
          console.log(`[OTA] Bundle available: ${latestVersion} (mode: ${bundle.mode || 'silent'})`);
        }
      }

      // ── 2. 네이티브 앱 버전 체크 (필요한 경우에만) ──
      if (needsNativeCheck) {
        const configRef = doc(Fire.store, 'ota', 'config');
        const configSnap = await getDoc(configRef);

        if (configSnap.exists()) {
          const config = configSnap.data();
          const storeUrl = this.platform === 'ios'
            ? config.storeUrls?.ios || ''
            : config.storeUrls?.android || '';

          if (config.minNativeVersion && compareVersions(this.nativeVersion, config.minNativeVersion) < 0) {
            this._nativeStatus = { type: 'forced', storeUrl };
            console.log(`[OTA] FORCED — app ${this.nativeVersion} < min ${config.minNativeVersion}`);
          } else if (config.recommendedNativeVersion && compareVersions(this.nativeVersion, config.recommendedNativeVersion) < 0) {
            this._nativeStatus = { type: 'recommended', storeUrl };
            console.log(`[OTA] RECOMMENDED — app ${this.nativeVersion} < recommended ${config.recommendedNativeVersion}`);
          }
        }
      }

      const t2 = performance.now();
      console.log(`[OTA] Check timing — releasesQuery: ${(t1-t0).toFixed(0)}ms${needsNativeCheck ? `, configDoc: ${(t2-t1).toFixed(0)}ms` : ''}, total: ${(t2-t0).toFixed(0)}ms`);
    } catch (error) {
      console.error('[OTA] Check failed:', error);
    }
  }

  /**
   * OTA 번들 다운로드 + 적용
   * 
   * 모든 모드 공통: 다운로드 진행률 표시 → 즉시 적용 → 새로고침
   * - silent: 팝업 없이 토스트만 (자동 실행)
   * - flexible: 사용자 확인 후 호출
   * - forced: 팝업 차단 후 자동 실행
   * 
   * @returns 적용 성공 여부
   */
  async downloadAndApply(): Promise<boolean> {
    if (this._bundleStatus.type !== 'available') return false;

    const { fullVersion, url, checksum } = this._bundleStatus.bundle;

    // 업데이트 화면이 없으면 표시 (flexible 모드에서 직접 호출 시)
    if (!document.getElementById('ota-update-screen')) {
      showUpdateScreen();
    }

    // 다운로드 진행률 리스너
    const progressListener = await CapacitorUpdater.addListener(
      'download',
      (state: { percent: number }) => {
        updateProgress(state.percent);
      }
    );

    try {
      console.log(`[OTA] Downloading bundle ${fullVersion}...`);

      // 1. 번들 다운로드 (진행률은 리스너에서 표시)
      const downloaded: BundleInfo = await CapacitorUpdater.download({
        url,
        version: fullVersion,
        checksum,
      });

      console.log(`[OTA] Downloaded: ${downloaded.id} (${downloaded.version})`);
      progressListener.remove();

      // 2. 적용 화면 표시
      updateProgress(100);
      this.saveBundleVersion(fullVersion);

      if (this.platform === 'ios') {
        // iOS: set() 후 강제 페이지 교체
        updateStatus(t.applying, t.soon);
        await new Promise(r => setTimeout(r, 500));
        localStorage.setItem('ota-reloaded', '1');
        await CapacitorUpdater.set(downloaded);
        // set()이 WebView를 리로드하지 못할 경우 강제 페이지 교체
        await new Promise(r => setTimeout(r, 500));
        window.location.replace(window.location.href);
      } else {
        // Android: set()으로 즉시 적용
        updateStatus(t.applying, t.soon);
        await new Promise(r => setTimeout(r, 500));
        localStorage.setItem('ota-reloaded', '1');
        await CapacitorUpdater.set(downloaded);
      }

      return true;
    } catch (error) {
      console.error(`[OTA] Download/Apply failed:`, error);
      progressListener.remove();
      hideUpdateScreen();
      this.removeSplash();
      return false;
    }
  }

  /**
   * 전체 OTA 프로세스 실행 (init 후 자동 호출용)
   * 
   * 1. Firestore 체크
   * 2. 번들 있으면 다운로드
   * 3. silent이면 다운로드만, flexible이면 UI에 위임
   */
  async runUpdateFlow(): Promise<void> {
    if (this.updateFlowRan) return;
    this.updateFlowRan = true;

    // ── 이전 세션에서 백그라운드 다운로드된 번들이 있으면 즉시 적용 ──
    const pending = localStorage.getItem('ota-pending-bundle');
    if (pending) {
      try {
        const bundleInfo = JSON.parse(pending);
        localStorage.removeItem('ota-pending-bundle');
        console.log(`[OTA] Applying pending bundle: ${bundleInfo.version}`);
        showUpdateScreen();
        updateStatus(t.applying, t.soon);
        localStorage.setItem('ota-reloaded', '1');
        await CapacitorUpdater.set(bundleInfo);
        // set() 후 리로드됨 → 여기까지 오면 실패
        await new Promise(r => setTimeout(r, 500));
        window.location.replace(window.location.href);
        return;
      } catch (e) {
        console.warn('[OTA] Failed to apply pending bundle:', e);
        localStorage.removeItem('ota-pending-bundle');
        hideUpdateScreen();
      }
    }

    await this.checkForUpdates();

    // 네이티브 업데이트가 필요하면 번들 즉시 적용은 중단 (UI가 팝업을 표시함)
    // 단, 번들 상태는 보존 → 사용자가 "나중에" 시 downloadOnly() 가능
    if (this._nativeStatus.type !== 'up-to-date') {
      this.removeSplash();
      this._readyResolver(); // 앱 데이터 로딩 허용
      return;
    }

    // 번들 업데이트 확인
    if (this._bundleStatus.type !== 'available') {
      this.removeSplash();
      this._readyResolver(); // 업데이트 없음 → 앱 데이터 로딩 허용
      return;
    }

    const mode = this._bundleStatus.bundle.mode;

    if (mode === 'silent' || mode === 'forced') {
      // 번들 감지 즉시 업데이트 화면으로 전환 (사용자 UI 차단)
      showUpdateScreen();
      await this.downloadAndApply();
      // set() 후 리로드되므로 여기까지 오면 실패한 경우
      this._readyResolver();
    }
    // flexible: NativeUpdateModal에서 사용자에게 안내 후 downloadAndApply 호출
    // flexible은 사용자 동의 전까지 앱을 정상 사용하므로 즉시 resolve
    this._readyResolver();
  }

  /**
   * 번들을 백그라운드로 다운로드만 하고 적용하지 않음.
   * 다음 앱 실행 시 runUpdateFlow()에서 pending 번들로 감지하여 자동 적용.
   */
  async downloadOnly(): Promise<void> {
    if (this._bundleStatus.type !== 'available') return;

    const { fullVersion, url, checksum } = this._bundleStatus.bundle;
    console.log(`[OTA] Background download started: ${fullVersion}`);

    try {
      const downloaded: BundleInfo = await CapacitorUpdater.download({
        url,
        version: fullVersion,
        checksum,
      });

      // 다음 실행 시 적용하도록 저장
      localStorage.setItem('ota-pending-bundle', JSON.stringify({
        id: downloaded.id,
        version: downloaded.version,
      }));
      this.saveBundleVersion(fullVersion);
      console.log(`[OTA] Background download complete: ${fullVersion} (will apply on next launch)`);
    } catch (error) {
      console.error('[OTA] Background download failed:', error);
    }
  }

  // ── Getter ──

  getNativeUpdateStatus(): NativeUpdateStatus { return this._nativeStatus; }
  getBundleUpdateStatus(): BundleUpdateStatus { return this._bundleStatus; }

  /** index.html의 OTA 스플래시 제거 */
  private removeSplash(): void {
    const splash = document.getElementById('ota-splash');
    if (splash) splash.remove();
  }

  saveBundleVersion(version: string): void {
    this.currentBundleVersion = version;
    localStorage.setItem('ota-bundle-version', version);
  }

  get isInitialized(): boolean { return this.initialized; }
  get currentNativeVersion(): string { return this.nativeVersion; }
  get currentPlatform(): string { return this.platform; }
}

export const otaService = new OtaUpdateService();
